export interface Book {
  id: string;
  title: string;
  subtitle?: string;
  number: number;
  status: 'published' | 'upcoming' | 'writing' | 'in_progress' | 'planned';
  publishDate?: string;
  description: string;
  shortDescription: string;
  coverImage: string;
  coverImageAlt: string;
  amazonLink?: string;
  goodreadsLink?: string;
  wordCount?: number;
  themes: string[];
  mainCharacters: string[];
  setting: string[];
  triggerWarnings?: string[];
  excerpt?: string;
}

export const books: Book[] = [
  {
    id: 'fractured-recall',
    title: 'Fractured Recall',
    subtitle: 'Memory Keeper Mysteries Book 1',
    number: 1,
    status: 'published',
    publishDate: '2024-09-15',
    description: `When the past refuses to stay buried, some memories are too dangerous to remember.

Psychologist Dr. Sam Rivers thought Cedar Falls would be the perfect place to start over—a quiet Pacific Northwest town where he could help people heal from everyday trauma. But when client Claire Morrison begins recovering suppressed memories of childhood witnesses to murder, Sam realizes he's stumbled into something far more sinister.

Twenty years ago, eight-year-old Claire saw something she was never meant to see. Now, as her memories surface with terrifying clarity, she can identify faces, voices, and a conspiracy that reaches into the heart of Cedar Falls' most trusted institutions.

Father Michael Kozlov has spent two decades building his reputation as a beloved community leader. But underneath his charitable facade lies a calculating killer who has been systematically eliminating anyone who threatens to expose his crimes. When Claire's recovered memories make her a target, Sam must choose between his professional ethics and keeping his client alive.

In a town where everyone knows everyone else's secrets, some truths are worth killing for.

From M.K. Rivers comes a gripping psychological thriller that explores the dangerous intersection of memory, truth, and survival. Perfect for fans of Gillian Flynn, Tana French, and Ruth Ware.`,
    shortDescription: `When psychologist Dr. Sam Rivers helps client Claire Morrison recover suppressed childhood memories, they uncover a decades-old conspiracy that powerful people will kill to protect. In the fog-shrouded town of Cedar Falls, some memories are too dangerous to remember.`,
    coverImage: '/book-covers/fractured-recall-cover.jpg',
    coverImageAlt: 'Fractured Recall book cover showing a dark house with a single lit window against a foggy backdrop',
    amazonLink: 'https://www.amazon.com/dp/B0FPDHTNCK',
    goodreadsLink: 'https://www.goodreads.com/book/show/241065798-fractured-recall',
    wordCount: 46139,
    themes: [
      'Memory Recovery',
      'Small Town Secrets',
      'Institutional Corruption',
      'Trust and Betrayal',
      'Professional Ethics vs. Moral Responsibility'
    ],
    mainCharacters: [
      'Dr. Sam Rivers',
      'Claire Morrison',
      'Detective Maya Chen',
      'Father Michael Kozlov',
      'Dr. Elena Vasquez'
    ],
    setting: [
      'Cedar Falls, Washington',
      'Pacific Northwest',
      'St. Andrew\'s Church',
      'Cedar Falls Police Station',
      'Murphy House'
    ],
    triggerWarnings: [
      'Violence',
      'Murder',
      'Childhood trauma',
      'Psychological manipulation'
    ],
    excerpt: `The brass nameplate felt heavier than it should have in my hands. *Dr. Sam Rivers, Licensed Clinical Psychologist.* Simple black letters on gold-toned metal, waiting to be mounted on the door of what would either be my salvation or my final professional failure.

I set the nameplate on the oak desk and stepped back to survey my new office. The Victorian house on Maple Street had good bones—high ceilings, original hardwood floors, and tall windows that let in the kind of diffused Pacific Northwest light that made everything look like a watercolor painting...`
  },
  {
    id: 'blackwood-mansion',
    title: 'The Blackwood Mansion',
    subtitle: 'Memory Keeper Mysteries Book 2',
    number: 2,
    status: 'in_progress',
    publishDate: '2025-10-01',
    description: `The past refuses to stay buried at Cedar Falls' most infamous house.

Six months after the Father Michael case, Dr. Sam Rivers thought he could return to quiet therapeutic work. But when Claire Morrison starts experiencing nightmares about the abandoned Blackwood mansion, Sam realizes that Cedar Falls' dark history runs deeper than anyone imagined.

The Blackwood family massacre of 1987 was Cedar Falls' most notorious crime—an entire family found dead in their Victorian mansion, with only young Timothy Blackwood surviving to tell a story no one believed. Now, decades later, someone is recreating the murders with chilling precision.

As Sam and Detective Maya Chen investigate the copycat killings, they uncover a web of generational trauma, inherited guilt, and family secrets that connect the original Blackwood murders to a conspiracy that reaches into the present. But the closer they get to the truth, the more dangerous their investigation becomes.

Because some families will do anything to keep their darkest secrets buried—even if it means killing again.`,
    shortDescription: `When copycat murders mirror the infamous Blackwood family massacre from 1987, Dr. Sam Rivers and Detective Maya Chen must uncover generational secrets that some families will kill to protect.`,
    coverImage: '/covers/book2.png',
    coverImageAlt: 'The Blackwood Mansion book cover showing a Gothic Victorian mansion silhouetted against storm clouds',
    amazonLink: 'https://amazon.com/dp/B0DXXX456Y',
    goodreadsLink: 'https://goodreads.com/book/show/987654321',
    wordCount: 48000,
    themes: [
      'Generational Trauma',
      'Family Secrets',
      'Inherited Guilt',
      'Gothic Horror Elements',
      'Historical Mysteries'
    ],
    mainCharacters: [
      'Dr. Sam Rivers',
      'Claire Morrison',
      'Detective Maya Chen',
      'Timothy Blackwood',
      'Elena Vasquez'
    ],
    setting: [
      'Blackwood Mansion',
      'Cedar Falls Historical Society',
      'Blackwood Family Cemetery',
      'Cedar Falls Archives'
    ]
  },
  {
    id: 'echoes-of-silence',
    title: 'Echoes of Silence',
    subtitle: 'Memory Keeper Mysteries Book 3',
    number: 3,
    status: 'planned',
    publishDate: '2026-01-01',
    description: `A missing children case from the 1990s surfaces when survivors now adults begin recovering memories of systematic abuse at a religious commune near Cedar Falls. Sam must navigate cult psychology and mass memory manipulation while protecting witnesses from those who want the truth buried forever.`,
    shortDescription: `Adult survivors of a 1990s religious commune begin recovering memories of systematic abuse, forcing Dr. Sam Rivers to confront cult psychology and mass memory manipulation.`,
    coverImage: '/book-covers/echoes-silence-cover.jpg',
    coverImageAlt: 'Echoes of Silence book cover showing shadowy figures in a forest clearing',
    themes: [
      'Cult Psychology',
      'Mass Memory Manipulation',
      'Religious Trauma',
      'Group Dynamics',
      'Survivor Advocacy'
    ],
    mainCharacters: [
      'Dr. Sam Rivers',
      'Claire Morrison',
      'Detective Maya Chen',
      'Cult Survivors',
      'Elena Vasquez'
    ],
    setting: [
      'Former commune site',
      'Cedar Falls outskirts',
      'Forest commune buildings'
    ]
  },
  {
    id: 'memory-palace',
    title: 'The Memory Palace',
    subtitle: 'Memory Keeper Mysteries Book 4',
    number: 4,
    status: 'planned',
    publishDate: '2026-07-01',
    description: `An identity theft ring uses stolen memories and psychological profiles to create false identities. Sam faces his most challenging case yet when the criminals begin using technology-assisted memory manipulation, blurring the lines between authentic memories and implanted experiences.`,
    shortDescription: `When criminals use technology-assisted memory manipulation for identity theft, Dr. Sam Rivers must question the nature of memory and identity itself.`,
    coverImage: '/book-covers/memory-palace-cover.jpg',
    coverImageAlt: 'The Memory Palace book cover showing fragments of memories floating in a digital space',
    themes: [
      'Identity and Memory',
      'Technology Ethics',
      'False Memory Implantation',
      'Digital Crime',
      'Nature of Self'
    ],
    mainCharacters: [
      'Dr. Sam Rivers',
      'Claire Morrison',
      'Detective Maya Chen',
      'Tech criminals',
      'Elena Vasquez'
    ],
    setting: [
      'Tech facilities',
      'Digital crime scenes',
      'Memory research labs'
    ]
  },
  {
    id: 'ritual-of-forgetting',
    title: 'The Ritual of Forgetting',
    subtitle: 'Memory Keeper Mysteries Book 5',
    number: 5,
    status: 'planned',
    publishDate: '2027-01-01',
    description: `When elderly residents of Cedar Falls begin experiencing sudden memory loss, Sam discovers a connection to a secret therapy program that may be deliberately erasing traumatic memories. But some memories are meant to be kept, even the painful ones.`,
    shortDescription: `Elderly residents begin losing their memories in a pattern that suggests deliberate intervention, forcing Dr. Sam Rivers to question whether some forgotten traumas should stay buried.`,
    coverImage: '/book-covers/ritual-forgetting-cover.jpg',
    coverImageAlt: 'The Ritual of Forgetting book cover showing a brain scan dissolving into mist',
    themes: [
      'Elder Care Ethics',
      'Medical Malpractice',
      'Memory Preservation',
      'Generational Trauma',
      'Therapeutic Boundaries'
    ],
    mainCharacters: [
      'Dr. Sam Rivers',
      'Claire Morrison',
      'Detective Maya Chen',
      'Elder Care Residents',
      'Elena Vasquez'
    ],
    setting: [
      'Cedar Falls Care Center',
      'Memory Research Facility',
      'Retirement Community'
    ]
  },
  {
    id: 'whispers-in-darkness',
    title: 'Whispers in the Darkness',
    subtitle: 'Memory Keeper Mysteries Book 6',
    number: 6,
    status: 'planned',
    publishDate: '2027-07-01',
    description: `A series of sleepwalking incidents leads Sam to uncover a network of people sharing the same recurring nightmare. As the boundary between dreams and reality blurs, Sam must solve a mystery that exists in the space between sleeping and waking.`,
    shortDescription: `When multiple residents share identical nightmares that lead to dangerous sleepwalking episodes, Dr. Sam Rivers enters the murky world where dreams and memories intersect.`,
    coverImage: '/book-covers/whispers-darkness-cover.jpg',
    coverImageAlt: 'Whispers in the Darkness book cover showing shadowy figures walking through a moonlit forest',
    themes: [
      'Dream Psychology',
      'Shared Consciousness',
      'Sleep Disorders',
      'Collective Memory',
      'Subconscious Manipulation'
    ],
    mainCharacters: [
      'Dr. Sam Rivers',
      'Claire Morrison',
      'Detective Maya Chen',
      'Sleep Study Patients',
      'Elena Vasquez'
    ],
    setting: [
      'Sleep Research Lab',
      'Dream Analysis Center',
      'Patients\' Homes'
    ]
  },
  {
    id: 'fractured-timeline',
    title: 'The Fractured Timeline',
    subtitle: 'Memory Keeper Mysteries Book 7',
    number: 7,
    status: 'planned',
    publishDate: '2028-01-01',
    description: `Time seems to be playing tricks in Cedar Falls as residents begin experiencing memories from different time periods simultaneously. Sam must unravel a conspiracy that spans decades and threatens to rewrite the town's entire history.`,
    shortDescription: `When residents begin experiencing memories from multiple time periods at once, Dr. Sam Rivers discovers a decades-spanning conspiracy that could rewrite Cedar Falls' history.`,
    coverImage: '/book-covers/fractured-timeline-cover.jpg',
    coverImageAlt: 'The Fractured Timeline book cover showing clock faces dissolving into historical photographs',
    themes: [
      'Temporal Displacement',
      'Historical Revision',
      'Memory Chronology',
      'Time Perception',
      'Historical Conspiracy'
    ],
    mainCharacters: [
      'Dr. Sam Rivers',
      'Claire Morrison',
      'Detective Maya Chen',
      'Historical Society Members',
      'Elena Vasquez'
    ],
    setting: [
      'Cedar Falls Archives',
      'Historical Society',
      'Old Town District'
    ]
  },
  {
    id: 'final-recall',
    title: 'Final Recall',
    subtitle: 'Memory Keeper Mysteries Book 8',
    number: 8,
    status: 'planned',
    publishDate: '2028-07-01',
    description: `In the series finale, all of Cedar Falls' buried secrets converge as Sam faces his greatest challenge yet. With the entire conspiracy finally exposed, he must decide what memories are worth preserving and which ones should be forgotten forever.`,
    shortDescription: `In the explosive series finale, Dr. Sam Rivers confronts the master conspiracy behind all of Cedar Falls' dark secrets, forcing him to make the ultimate choice about memory, truth, and justice.`,
    coverImage: '/book-covers/final-recall-cover.jpg',
    coverImageAlt: 'Final Recall book cover showing Cedar Falls emerging from lifting fog with all secrets revealed',
    themes: [
      'Ultimate Truth',
      'Justice vs Mercy',
      'Memory as Legacy',
      'Community Healing',
      'Final Redemption'
    ],
    mainCharacters: [
      'Dr. Sam Rivers',
      'Claire Morrison',
      'Detective Maya Chen',
      'All Previous Antagonists',
      'Elena Vasquez'
    ],
    setting: [
      'All Previous Locations',
      'Cedar Falls Town Hall',
      'The Truth Chamber'
    ]
  }
];

export const getPublishedBooks = () => books.filter(book => book.status === 'published');
export const getUpcomingBooks = () => books.filter(book => book.status === 'upcoming' || book.status === 'in_progress');
export const getInProgressBooks = () => books.filter(book => book.status === 'in_progress');
export const getBookById = (id: string) => books.find(book => book.id === id);
export const getBookByNumber = (number: number) => books.find(book => book.number === number);
export const getLatestBook = () => books.filter(book => book.status === 'published').sort((a, b) => 
  new Date(b.publishDate || '').getTime() - new Date(a.publishDate || '').getTime()
)[0];